/**
 * Charge and billing schemas
 */
import { z } from "zod";
import { createEntityFilterSchema, NestedPaginationParamsSchema, PopulateQueryParamsSchema, SortParamsSchema, StrapiIdSchema, } from "./common.schemas";
import { ChargeRelationSchema, LazyBillingDataRelation, LazyBotConfigRelation, LazyPaymentRelation, LazyRefundRelation, LazySubscriptionRelation, LazySubscriptionTierRelation, LazyUserRelation, RefundRelationSchema, } from "./relations";
// ============================================================================
// Enums
// ============================================================================
/**
 * Charge payment type schema
 */
export const ChargePaymentTypeSchema = z.enum(["automatic", "manual"]);
/**
 * Charge line item type schema
 */
export const ChargeLineItemTypeSchema = z.enum(["vps", "subscription_tier", "profit_fee", "discount"]);
// VerifactuStatusSchema and PaymentProcessorSchema are imported from common.schemas.ts
/**
 * Charge/Payment status schema (injected by payments-manager plugin)
 * Kept in sync with Strapi enum at
 * packages/payments-manager/server/content-types/userDefinedOverrides.js
 * (charge.pm_status) and with ChargeRelationSchema.pm_status in relations.ts.
 */
export const ChargeStatusSchema = z.enum([
    "pending",
    "processing",
    "completed",
    "failed",
    "cancelled",
    "skipped",
]);
// ChargeRiskLevelSchema is imported from common.schemas.ts and re-exported above
// ============================================================================
// Input Schemas
// ============================================================================
/**
 * Charge filters schema - typesafe filters for charge queries
 * Created using createEntityFilterSchema to reuse field types from ChargeRelationSchema
 */
export const ChargeFiltersSchema = createEntityFilterSchema(ChargeRelationSchema, {
    scalars: ["pm_status", "pm_paymentProcessor", "pm_currency", "pm_periodStart", "pm_periodEnd", "verifactu_status"],
    relations: ["bot_config", "pm_user", "pm_subscription"],
});
/**
 * Charge query params schema
 * Accepts nested pagination format from frontend: { pagination: { page, pageSize } }
 */
export const ChargeQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .merge(PopulateQueryParamsSchema)
    .extend({
    filters: ChargeFiltersSchema.optional(),
})
    .strict();
/**
 * Refund filters schema - typesafe filters for refund queries
 * Created using createEntityFilterSchema to reuse field types from RefundRelationSchema
 */
export const RefundFiltersSchema = createEntityFilterSchema(RefundRelationSchema, {
    scalars: ["pm_status", "pm_paymentProcessor", "pm_currency", "verifactu_status"],
    relations: ["bot_config", "pm_user"],
});
/**
 * Refund query params schema
 * Accepts nested pagination format from frontend: { pagination: { page, pageSize } }
 */
export const RefundQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .merge(PopulateQueryParamsSchema)
    .extend({
    filters: RefundFiltersSchema.optional(),
})
    .strict();
/**
 * Admin refund input schema
 */
export const AdminRefundInputSchema = z
    .object({
    chargeId: StrapiIdSchema,
    password: z.string().min(1, "Password is required for refund authorization"),
    reason: z.string().min(1, "Refund reason is required"),
    amount: z.number().positive("Amount must be positive").optional(),
})
    .strict();
/**
 * Download invoice request schema
 */
export const DownloadInvoiceRequestSchema = z
    .object({
    chargeId: StrapiIdSchema,
})
    .strict();
/**
 * Download multiple invoices request schema
 */
export const DownloadInvoicesRequestSchema = z
    .object({
    chargeIds: z.array(StrapiIdSchema).min(1, "At least one charge ID is required"),
})
    .strict();
/**
 * Preview invoice line item schema
 */
export const PreviewInvoiceLineItemSchema = z.object({
    type: z.enum(["vps", "subscription_tier", "profit_fee", "discount"]),
    description: z.string(),
    baseAmount: z.number(),
    finalAmount: z.number(),
});
/**
 * Preview invoice billing data schema (legacy format)
 */
export const PreviewInvoiceBillingDataSchema = z.object({
    name: z.string().nullish(),
    tax_id: z.string().nullish(),
    line1: z.string().nullish(),
    line2: z.string().nullish(),
    postal_code: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    country: z.string().nullish(),
});
/**
 * Preview invoice input schema - matches PreviewInvoiceInput from generateInvoicePreview.ts
 */
export const PreviewInvoiceInputSchema = z
    .object({
    pm_amount: z.number(),
    pm_currency: z.string(),
    pm_reason: z.string(),
    pm_recipientBillingData: PreviewInvoiceBillingDataSchema.optional(),
    pm_issuerBillingData: PreviewInvoiceBillingDataSchema.optional(),
    bot_config: z
        .object({
        exchange_account: z
            .object({
            name: z.string().optional(),
        })
            .strict()
            .optional(),
    })
        .strict()
        .optional(),
    lineItems: z.array(PreviewInvoiceLineItemSchema).optional(),
    baseAmount: z.number().optional(),
    discountedAmount: z.number().optional(),
    previewId: z.string().optional(),
})
    .strict();
/**
 * Upcoming charges query params schema
 */
export const UpcomingChargesQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    daysAhead: z.number().int().min(0).max(365).optional(),
    filters: z
        .object({
        dueDate: z
            .object({
            $gte: z.string().optional(),
            $lte: z.string().optional(),
        })
            .strict()
            .optional(),
        "subscription.id": z.object({ $eq: StrapiIdSchema.optional() }).strict().optional(),
    })
        .strict()
        .optional(),
})
    .strict();
// ============================================================================
// Component Schemas
// ============================================================================
/**
 * Charge line item metadata schema
 */
export const ChargeLineItemMetadataSchema = z.object({
    discountId: StrapiIdSchema.nullish(),
    discountCode: z.string().nullish(),
    discountPercentage: z.number().nullish(),
    originalAmount: z.number().nullish(),
});
/**
 * Charge line item schema - matches misc.charge-line-item component
 */
export const ChargeLineItemSchema = z.object({
    type: ChargeLineItemTypeSchema,
    description: z.string(),
    baseAmount: z.number(),
    finalAmount: z.number(),
    metadata: ChargeLineItemMetadataSchema.nullish(),
});
/**
 * Tier snapshot schema
 */
export const TierSnapshotSchema = z.object({
    name: z.string().nullish(),
    code: z.string().nullish(),
    monthlyPrice: z.number().nullish(),
    currency: z.string().nullish(),
});
// ============================================================================
// Entity Schemas
// ============================================================================
/**
 * Refund entity schema - extends RefundRelationSchema with additional fields and relations
 */
export const RefundSchema = RefundRelationSchema.extend({
    // Additional fields not in relation schema
    pm_processor_refundId: z.string().nullish(),
    pm_issuerBillingData: LazyBillingDataRelation.nullish(),
    pm_recipientBillingData: LazyBillingDataRelation.nullish(),
    // Relations (populated)
    bot_config: LazyBotConfigRelation.nullish(),
    pm_user: LazyUserRelation.nullish(),
    pm_charge: z.lazy(() => z.object({ id: StrapiIdSchema }).passthrough()).nullish(),
});
/**
 * Charge entity schema - extends ChargeRelationSchema with additional fields and relations
 */
export const ChargeSchema = ChargeRelationSchema.extend({
    // Line items (component) - not in relation schema
    lineItems: z.array(ChargeLineItemSchema).nullish(),
    // Tier info - not in relation schema
    tierSnapshot: TierSnapshotSchema.nullish(),
    // Additional pm_* fields not in relation schema
    pm_processor_chargeId: z.string().nullish(),
    pm_issuerBillingData: LazyBillingDataRelation.nullish(),
    pm_recipientBillingData: LazyBillingDataRelation.nullish(),
    // Relations (populated)
    bot_config: LazyBotConfigRelation.nullish(),
    pm_user: LazyUserRelation.nullish(),
    subscription_tier: LazySubscriptionTierRelation.nullish(),
    pm_subscription: LazySubscriptionRelation.nullish(),
    pm_payments: z.array(LazyPaymentRelation).nullish(),
    pm_refunds: z.array(LazyRefundRelation).nullish(),
});
/**
 * Upcoming charge billing info schema
 */
export const UpcomingChargeBillingSchema = z.object({
    periodStart: z.string(),
    periodEnd: z.string(),
    periodReason: z.string(),
    dueDate: z.string(),
    daysUntilDue: z.number(),
    billableDays: z.number(),
    freeDays: z.number(),
    remainingFreeDays: z.number(),
    periodProfit: z.number(),
    profitCurrency: z.string(),
    feePercentage: z.number(),
    elapsedDays: z.number(),
    totalPeriodDays: z.number(),
    extrapolatedProfit: z.number(),
    extrapolatedAmount: z.number(),
});
/**
 * Upcoming charge line item schema - matches LineItem from calculateCharge.ts
 */
export const UpcomingChargeLineItemSchema = z.object({
    type: z.enum(["vps", "subscription_tier", "profit_fee", "discount"]),
    description: z.string(),
    baseAmount: z.number(),
    finalAmount: z.number(),
});
/**
 * Upcoming charge calculation result schema - matches ChargeCalculationResult
 */
export const UpcomingChargeCalculationSchema = z.object({
    targetTotal: z.number(),
    vpsCost: z.number(),
    tierCost: z.number(),
    profitFee: z.number(),
    lineItems: z.array(UpcomingChargeLineItemSchema),
    breakdown: z.object({
        fixedCosts: z.number(),
        variableCost: z.number(),
    }),
});
/**
 * Upcoming charge billable result schema - matches BillableRangesResult
 */
export const UpcomingChargeBillableResultSchema = z.object({
    billableDays: z.number(),
    freeDays: z.number(),
    totalDays: z.number(),
    isFullyFree: z.boolean(),
    billableRanges: z
        .array(z.object({
        from: z.string(),
        to: z.string(),
    }))
        .optional(),
    freeRanges: z
        .array(z.object({
        from: z.string(),
        to: z.string(),
    }))
        .optional(),
    activeDays: z.number().optional(),
    activeDates: z.array(z.string()).optional(),
    effectiveBillableDays: z.number().optional(),
});
/**
 * Upcoming charge estimate schema - matches EstimateChargeResult
 */
export const UpcomingChargeEstimateSchema = z.object({
    success: z.boolean(),
    error: z.string().optional(),
    billingStatus: z.enum(["ready", "validation_pending"]).optional(),
    ownership: z.object({
        status: z.enum(["verified", "held"]),
        includedDeals: z.number().int().nonnegative(),
        excludedDeals: z.number().int().nonnegative(),
        unverifiedDeals: z.number().int().nonnegative(),
        reasons: z.array(z.string()),
    }).optional(),
    periodStart: z.string(),
    periodEnd: z.string(),
    periodReason: z.string(),
    periodProfit: z.number(),
    profitCurrency: z.string(),
    feePercentage: z.number(),
    billableResult: UpcomingChargeBillableResultSchema,
    calculation: UpcomingChargeCalculationSchema,
    lineItems: z.array(UpcomingChargeLineItemSchema),
    baseAmount: z.number(),
    discountedAmount: z.number(),
    finalAmount: z.number(),
    availableDiscounts: z.array(z.object({
        id: StrapiIdSchema,
        amount: z.number(),
        unusedAmount: z.number(),
        potentialUse: z.number(),
    })),
    subscriptionTier: z
        .object({
        id: StrapiIdSchema,
        name: z.string(),
        code: z.string(),
        monthlyPrice: z.number(),
        vpsOverride: z.number(),
        dynamicFee: z.array(z.object({
            fee: z.number(),
            below: z.number().nullish(),
        })),
        vpsText: z.string().optional(),
        tierText: z.string().optional(),
        profitFeeText: z.string().optional(),
        discountText: z.string().optional(),
        commissionOnlyBilling: z.boolean(),
    })
        .optional(),
    tierSnapshot: z
        .object({
        id: StrapiIdSchema,
        name: z.string(),
        code: z.string(),
        monthlyPrice: z.number(),
        invoiceTexts: z.object({
            vps: z.string(),
            tier: z.string(),
            profitFee: z.string(),
            discount: z.string(),
        }),
    })
        .nullable(),
});
/**
 * Upcoming charge schema - matches API response from getUpcomingCharges
 */
export const UpcomingChargeSchema = z.object({
    status: z.enum(["ready", "verified_zero", "not_due", "validation_pending"]).optional(),
    statusReason: z.string().optional(),
    subscription: z.object({
        id: StrapiIdSchema,
        pm_currentPeriodStart: z.string(),
        pm_currentPeriodEnd: z.string(),
        pm_status: z.string(),
    }),
    user: z.object({
        id: StrapiIdSchema,
        email: z.string(),
        name: z.string(),
        pm_billingData: PreviewInvoiceBillingDataSchema.optional(),
    }),
    botConfig: z.object({
        id: StrapiIdSchema,
        uuid: z.string(),
        exchange_account: z.object({
            id: StrapiIdSchema,
            name: z.string(),
        }),
    }),
    billing: UpcomingChargeBillingSchema,
    estimate: UpcomingChargeEstimateSchema,
    estimatedAmount: z.number(),
    currency: z.string(),
});
/**
 * Upcoming charges summary schema
 */
export const UpcomingChargesSummarySchema = z.object({
    verifiedCharges: z.number().int().nonnegative().optional(),
    verifiedZeroCharges: z.number().int().nonnegative().optional(),
    notDueCharges: z.number().int().nonnegative().optional(),
    validationPendingCharges: z.number().int().nonnegative().optional(),
    totalCharges: z.number(),
    totalEstimatedAmount: z.number(),
    totalExtrapolatedAmount: z.number(),
    currency: z.string(),
});
/**
 * Upcoming charges response schema - full response from getUpcomingCharges endpoint
 */
export const UpcomingChargesResponseSchema = z.object({
    charges: z.array(UpcomingChargeSchema),
    summary: UpcomingChargesSummarySchema,
});
